/**
 * Budget Alerts — threshold-based spending alerts
 */

import React from 'react';
import { AlertTriangle, Info, CheckCircle2, Loader2 } from 'lucide-react';
import { useBudgetAlerts } from '../../hooks/useBudgetOverview';
import type { BudgetAlert } from '../../types/budget.types';

function AlertIcon({ severity }: { severity: BudgetAlert['severity'] }) {
  switch (severity) {
    case 'critical':
      return <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" aria-hidden />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" aria-hidden />;
    case 'success':
      return <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400" aria-hidden />;
    default:
      return <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" aria-hidden />;
  }
}

function getAlertStyles(severity: BudgetAlert['severity']): {
  container: string;
  icon: string;
  title: string;
  message: string;
  badge: string;
} {
  switch (severity) {
    case 'critical':
      return {
        container: 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800',
        icon: 'bg-red-100 dark:bg-red-900/40',
        title: 'text-red-800 dark:text-red-300',
        message: 'text-red-700 dark:text-red-400',
        badge: 'text-red-600 bg-red-100 dark:text-red-300 dark:bg-red-900/40',
      };
    case 'warning':
      return {
        container: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800',
        icon: 'bg-yellow-100 dark:bg-yellow-900/40',
        title: 'text-yellow-800 dark:text-yellow-300',
        message: 'text-yellow-700 dark:text-yellow-400',
        badge: 'text-yellow-600 bg-yellow-100 dark:text-yellow-300 dark:bg-yellow-900/40',
      };
    case 'success':
      return {
        container: 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800',
        icon: 'bg-blue-100 dark:bg-blue-900/40',
        title: 'text-blue-800 dark:text-blue-300',
        message: 'text-blue-700 dark:text-blue-400',
        badge: 'text-blue-600 bg-blue-100 dark:text-blue-300 dark:bg-blue-900/40',
      };
    default:
      return {
        container: 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800',
        icon: 'bg-orange-100 dark:bg-orange-900/40',
        title: 'text-orange-800 dark:text-orange-300',
        message: 'text-orange-700 dark:text-orange-400',
        badge: 'text-orange-600 bg-orange-100 dark:text-orange-300 dark:bg-orange-900/40',
      };
  }
}

export function BudgetAlerts(): React.ReactElement {
  const { data: alerts = [], isLoading } = useBudgetAlerts();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-purple-600" aria-label="Loading alerts" />
      </div>
    );
  }

  const criticalAlerts = alerts.filter((a) => a.severity !== 'success');

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Budget Alerts</h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {criticalAlerts.length} active {criticalAlerts.length === 1 ? 'alert' : 'alerts'}
        </span>
      </div>

      {alerts.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
          No active budget alerts. You&apos;re on track!
        </p>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => {
            const styles = getAlertStyles(alert.severity);
            return (
              <div
                key={alert.id}
                className={`flex items-center space-x-4 p-3 border rounded-lg ${styles.container}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${styles.icon}`}>
                  <AlertIcon severity={alert.severity} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${styles.title}`}>{alert.title}</p>
                  <p className={`text-sm ${styles.message}`}>{alert.message}</p>
                </div>
                {alert.thresholdLabel && (
                  <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${styles.badge}`}>
                    {alert.thresholdLabel}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
